import React, { useMemo, useRef, useState } from "react";
import DataTable from "examples/Tables/DataTable";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import LinearProgress from "@mui/material/LinearProgress";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { CircularProgress, FormControl, MenuItem, Select, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import * as Sentry from "@sentry/browser";
import { call } from "services/axiosService/core";
import {
  readWorkbook,
  validateRows,
  groupProblems,
  buildLearnerRecords,
} from "pages/AthleteProfile/bulkUploadParsing";

/* Concierge onboarding requests: bulk uploads over the instant limit that a
   coach/parent sent to our team. Admins download the file, review/correct it,
   then import it here on the uploader's behalf. The import posts sequential
   chunks to /users/bulk-upload; the API serializes them with a lock, advances
   `imported_rows` after each chunk (so a failed import resumes where it
   stopped) and keeps one invite reminder for the whole import. */

// Must not exceed the API's BULK_UPLOAD_MAX_ROWS.
const CHUNK_SIZE = 50;

const STATUS_LABELS = {
  pending: "Pending",
  in_review: "In review",
  importing: "Importing",
  imported: "Imported",
  rejected: "Rejected",
};

const PROBLEM_LABELS = {
  missingField: "Missing required field",
  invalidGender: "Gender not Male/Female",
  invalidDOB: "Invalid date of birth",
  invalidAge: "Age out of range",
  parentEmailForAdult: "Parent email on 18+ athlete",
  sameRow: "Athlete email = parent email",
  invalidEmail: "Invalid email",
  repeated: "Email repeated across rows",
  sharedParent: "Shared-account parent email reused",
};

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const num = (v) => Number(v || 0);

const sha256Hex = async (buffer) => {
  const digest = await crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
};

export function ConciergeUploadsTable({ requests, loadRequests }) {
  const [selectedId, setSelectedId] = useState("");
  const [pageNumber, setPageNumber] = useState(0);
  const selected = requests.find((r) => r.id === selectedId);

  const tableData = {
    columns: [
      { Header: "Date", accessor: "date", width: "14%" },
      { Header: "Uploader", accessor: "uploader_email", width: "26%" },
      { Header: "Type", accessor: "type", width: "10%" },
      { Header: "Athletes", accessor: "row_count", width: "10%" },
      { Header: "Warnings", accessor: "warning_count", width: "10%" },
      { Header: "Status", accessor: "status_label", width: "15%" },
      { Header: "Imported", accessor: "progress", width: "15%" },
    ],
    rows: requests.map((r) => ({
      ...r,
      date: (r.created_at || "").slice(0, 10),
      type: r.added_as === "asCoach" ? "Coach" : "Parent",
      status_label: STATUS_LABELS[r.status] || r.status,
      progress: `${num(r.imported_rows)} / ${num(r.row_count)}`,
    })),
  };

  return (
    <>
      <MDTypography variant="caption" color="text.secondary" display="block" mb={1}>
        Bulk uploads over the instant limit, sent by coaches and parents for concierge onboarding. Click a row to download, review and import it.
      </MDTypography>
      <DataTable table={tableData} setSelectedId={setSelectedId} pageNumber={pageNumber} setPageNumber={setPageNumber} canSearch />
      {selected && (
        <ConciergeRequestDialog
          request={selected}
          onClose={() => { setSelectedId(""); loadRequests(); }}
          onChanged={loadRequests}
        />
      )}
    </>
  );
}

function ConciergeRequestDialog({ request, onClose, onChanged }) {
  const [status, setStatus] = useState(request.status);
  const [notes, setNotes] = useState(request.notes || "");
  const [paymentArrangement, setPaymentArrangement] = useState(request.payment_arrangement || "");
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState(null); // { severity, message }

  // Import state
  const fileInputRef = useRef(null);
  const [checking, setChecking] = useState(false);
  const [parsed, setParsed] = useState(null); // { fileName, rows, problems, matchesStored, file }
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ done: num(request.imported_rows), created: 0, skipped: 0 });
  const [importError, setImportError] = useState("");
  const stopRef = useRef(false);

  // A corrected file can change the row count; the checked file is authoritative.
  const total = parsed && parsed.rows.length ? parsed.rows.length : num(request.row_count);
  const started = num(request.imported_rows) > 0;
  const finished = progress.done >= total && total > 0;
  const notify = (severity, message) => setSnack({ severity, message });

  const download = async () => {
    const r = await call(`users/bulk-upload/concierge/${request.id}/download`, "GET", {});
    if (r.status === 200) window.open(r.data.url, "_blank", "noopener");
    else notify("error", r?.data?.message || "Could not create a download link.");
  };

  const save = async (fields) => {
    setSaving(true);
    const r = await call(`users/bulk-upload/concierge/${request.id}`, "PATCH", { body: fields });
    setSaving(false);
    if (r.status === 200) { notify("success", "Saved."); onChanged(); return true; }
    notify("error", r?.data?.message || "Could not save.");
    return false;
  };

  const checkFile = (event) => {
    const file = event.target.files[0];
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!file) return;
    setChecking(true); setParsed(null); setImportError("");
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const { headerMatches, rows } = readWorkbook(e.target.result, request.added_as);
        const result = validateRows(rows, request.added_as);
        const hash = await sha256Hex(e.target.result);
        setParsed({
          file,
          fileName: file.name,
          headerMatches,
          rows,
          validRows: result.validRows,
          problems: groupProblems(result),
          matchesStored: hash === request.file_sha256,
        });
      } catch (err) {
        Sentry.captureException(err);
        notify("error", "Could not read that file.");
      } finally { setChecking(false); }
    };
    reader.readAsArrayBuffer(file);
  };

  const fileReady = parsed && parsed.headerMatches && parsed.problems.length === 0 && parsed.rows.length > 0;
  // Once an import has started, it must continue from the exact file it began with:
  // imported_rows is a position in that file.
  const fileMismatchAfterStart = started && parsed && !parsed.matchesStored;

  const runImport = async () => {
    if (!fileReady || fileMismatchAfterStart || !paymentArrangement) return;
    setImporting(true); setImportError(""); stopRef.current = false;
    try {
      if (!started && !parsed.matchesStored) {
        // Corrected file: store it as the request's file before importing, so
        // what was imported is what's on record.
        const form = new FormData();
        form.append("file", parsed.file);
        form.append("row_count", String(parsed.rows.length));
        const r = await call(`users/bulk-upload/concierge/${request.id}/file`, "POST", { body: form });
        if (r.status !== 200) { setImportError(r?.data?.message || "Could not store the corrected file."); return; }
      }
      const uploader = await call(`users/${request.uploader_id}`, "GET", {});
      if (uploader.status !== 200) { setImportError("Could not load the uploader's account."); return; }
      const learners = buildLearnerRecords(parsed.validRows, request.added_as, uploader.data);
      if (learners.length !== parsed.rows.length) { setImportError("Some rows could not be converted; re-check the file."); return; }

      let done = num(request.imported_rows), created = 0, skipped = 0;
      while (done < learners.length) {
        if (stopRef.current) break;
        const chunk = learners.slice(done, done + CHUNK_SIZE);
        const r = await call("users/bulk-upload", "POST", {
          body: {
            learners: chunk,
            added_as: request.added_as,
            guardian_id: request.uploader_id,
            subscription_type: request.subscription_type || "maxu_yearly",
            concierge_request_id: request.id,
            payment_arrangement: paymentArrangement,
            chunk_start: done,
          },
        });
        if (r.status !== 200) {
          setImportError(`Stopped at row ${done + 1} of ${learners.length}: ${r?.data?.message || `HTTP ${r.status}`}. Wait a minute, then Resume.`);
          Sentry.captureException(new Error("Concierge import chunk failed"), { extra: { request_id: request.id, chunk_start: done, status: r.status } });
          break;
        }
        created += (r.data.chunk_learner_ids || []).length;
        skipped += (r.data.existing_learners || []).length;
        done = num(r.data.concierge_request?.imported_rows) || done + chunk.length;
        setProgress({ done, created, skipped });
      }
      if (done >= learners.length) notify("success", `Import complete: ${created} created, ${skipped} skipped (already registered).`);
    } finally {
      setImporting(false);
      onChanged();
    }
  };

  const warningSummary = useMemo(() => request.warning_summary || [], [request]);

  return (
    <Dialog open onClose={importing ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Concierge upload — {request.uploader_email}</DialogTitle>
      <DialogContent>
        <MDBox mb={2}>
          <MDTypography variant="body2"><b>Uploader:</b> {request.uploader_name || "—"} ({request.uploader_email}) · id {request.uploader_id}</MDTypography>
          <MDTypography variant="body2"><b>Added as:</b> {request.added_as === "asCoach" ? "Coach" : "Parent"} · <b>Plan:</b> {request.subscription_type || "—"}</MDTypography>
          <MDTypography variant="body2"><b>File:</b> {request.original_filename} · <b>Athletes:</b> {total} · <b>Rows with warnings at upload:</b> {num(request.warning_count)}</MDTypography>
          <MDTypography variant="body2"><b>Received:</b> {(request.created_at || "").replace("T", " ").slice(0, 16)} UTC{request.updated_by ? ` · last updated by ${request.updated_by}` : ""}</MDTypography>
          {warningSummary.length > 0 && (
            <MDBox mt={1}>
              {warningSummary.map((w) => (
                <MDTypography key={w.type} variant="caption" display="block">
                  • {PROBLEM_LABELS[w.type] || w.type}: rows {(w.rows || []).slice(0, 30).join(", ")}{(w.rows || []).length > 30 ? "…" : ""}
                </MDTypography>
              ))}
            </MDBox>
          )}
          <MDButton variant="outlined" color="info" size="small" sx={{ mt: 1.5 }} onClick={download}>Download file</MDButton>
        </MDBox>

        <MDBox mb={2} display="flex" gap={2} alignItems="flex-start" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <MDTypography variant="caption" fontWeight="bold">Status</MDTypography>
            <Select value={status} onChange={(e) => setStatus(e.target.value)} sx={{ height: 38 }}>
              {Object.entries(STATUS_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </Select>
          </FormControl>
          <MDBox flex={1} minWidth={260}>
            <MDTypography variant="caption" fontWeight="bold">Notes</MDTypography>
            <TextField fullWidth multiline minRows={2} size="small" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </MDBox>
          <MDButton variant="contained" color="info" size="small" sx={{ mt: 2.5 }} disabled={saving || importing} onClick={() => save({ status, notes })}>
            <span translate="no" className="notranslate">{saving ? <CircularProgress size={16} color="inherit" /> : "Save"}</span>
          </MDButton>
        </MDBox>

        <MDBox p={2} sx={{ border: "1px solid #e0e0e0", borderRadius: "12px" }}>
          <MDTypography variant="h6" mb={1}>Import on the uploader&apos;s behalf</MDTypography>
          <MDTypography variant="caption" display="block" mb={1.5}>
            Choose the downloaded file (or your corrected copy). It is checked with the same rules as an instant upload and must have no problems.
            {started ? " This import has started, so you must choose the same file it started with." : " A corrected file replaces the stored one; the uploader's original is kept."}
          </MDTypography>
          <MDButton variant="outlined" color="dark" size="small" component="label" disabled={checking || importing || finished}>
            {checking ? "Checking…" : "Choose file to import"}
            <input type="file" hidden ref={fileInputRef} accept=".xlsx" onChange={checkFile} />
          </MDButton>

          {parsed && (
            <MDBox mt={1.5}>
              <MDTypography variant="body2"><b>{parsed.fileName}</b>: {parsed.rows.length} rows · {parsed.matchesStored ? "same as stored file" : "differs from stored file"}</MDTypography>
              {!parsed.headerMatches && <MDTypography variant="caption" color="error" display="block">Header row doesn&apos;t match the {request.added_as === "asCoach" ? "coach" : "parent"} template.</MDTypography>}
              {parsed.problems.map((p) => (
                <MDTypography key={p.type} variant="caption" color="error" display="block">
                  • {PROBLEM_LABELS[p.type]}: rows {p.rows.slice(0, 50).join(", ")}{p.rows.length > 50 ? ` …and ${p.rows.length - 50} more` : ""}
                </MDTypography>
              ))}
              {fileMismatchAfterStart && <MDTypography variant="caption" color="error" display="block">This is not the file this import started with.</MDTypography>}
            </MDBox>
          )}

          <MDBox mt={2} display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <MDTypography variant="caption" fontWeight="bold">Payment</MDTypography>
              <Select value={paymentArrangement} displayEmpty disabled={importing || started} onChange={(e) => setPaymentArrangement(e.target.value)} sx={{ height: 38 }}>
                <MenuItem value="" disabled>Select…</MenuItem>
                <MenuItem value="settled_offline">Settled offline / invoiced (mark paid)</MenuItem>
                <MenuItem value="pending">Leave payment pending</MenuItem>
              </Select>
            </FormControl>
            <MDButton variant="contained" color="success" sx={{ mt: 2 }} disabled={!fileReady || fileMismatchAfterStart || !paymentArrangement || importing || finished} onClick={runImport}>
              {started ? "Resume import" : "Start import"}
            </MDButton>
            {importing && (
              <MDButton variant="outlined" color="error" sx={{ mt: 2 }} onClick={() => { stopRef.current = true; }}>
                Stop after this chunk
              </MDButton>
            )}
          </MDBox>

          {(importing || progress.done > 0) && (
            <MDBox mt={2}>
              <LinearProgress variant="determinate" value={total ? Math.min(100, (100 * progress.done) / total) : 0} />
              <MDTypography variant="caption" display="block" mt={0.5}>
                {progress.done} / {total} rows processed{progress.created || progress.skipped ? ` · this session: ${progress.created} created, ${progress.skipped} already registered` : ""}
              </MDTypography>
              {importing && <MDTypography variant="caption" color="warning" display="block">Keep this window open until the import finishes.</MDTypography>}
            </MDBox>
          )}
          {importError && <MDTypography variant="caption" color="error" display="block" mt={1}>{importError}</MDTypography>}
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose} disabled={importing} color="secondary">Close</MDButton>
      </DialogActions>
      <Snackbar open={!!snack} autoHideDuration={5000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        {snack ? <Alert onClose={() => setSnack(null)} severity={snack.severity} sx={{ width: "100%" }}>{snack.message}</Alert> : <span />}
      </Snackbar>
    </Dialog>
  );
}
